import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getRoot,
  $isElementNode,
  $isTextNode,
  LexicalNode,
  TextNode,
} from 'lexical';
import { useEffect } from 'react';
import { createHighlightRegex } from './highlight-utils';
import { HighlightColor, HighlightPattern, highlightStyles } from './types';

type CompiledPattern = {
  regex: RegExp;
  color?: HighlightColor;
  priority: number;
};

type Match = {
  start: number;
  end: number;
  color?: HighlightColor;
  priority: number;
};

const traverseTextNodes = (
  node: LexicalNode,
  visit: (textNode: TextNode) => void,
): void => {
  if ($isTextNode(node)) {
    visit(node);
    return;
  }
  if ($isElementNode(node)) {
    for (const child of node.getChildren()) traverseTextNodes(child, visit);
  }
};

const compilePatterns = (patterns: HighlightPattern[]): CompiledPattern[] => {
  const byRegex = new Map<string, CompiledPattern>();
  for (const pattern of patterns) {
    if (!pattern.regex) continue;
    const priority = pattern.priority ?? 0;
    const existing = byRegex.get(pattern.regex);
    if (existing && existing.priority >= priority) continue;
    const regex = createHighlightRegex(pattern.regex);
    if (!regex) continue;
    byRegex.set(pattern.regex, { regex, color: pattern.color, priority });
  }
  return Array.from(byRegex.values());
};

const collectMatches = (text: string, patterns: CompiledPattern[]): Match[] => {
  const rawMatches: Match[] = [];
  for (const { regex, color, priority } of patterns) {
    regex.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = regex.exec(text)) !== null) {
      if (!m[0]) {
        regex.lastIndex++;
        continue;
      }
      rawMatches.push({
        start: m.index,
        end: m.index + m[0].length,
        color,
        priority,
      });
    }
  }
  if (rawMatches.length === 0) return rawMatches;

  const breakpoints = new Set<number>();
  for (const m of rawMatches) {
    breakpoints.add(m.start);
    breakpoints.add(m.end);
  }
  const sortedPoints = [...breakpoints].sort((a, b) => a - b);

  const segments: Match[] = [];
  for (let i = 0; i < sortedPoints.length - 1; i++) {
    const segStart = sortedPoints[i];
    const segEnd = sortedPoints[i + 1];
    let winner: Match | undefined;
    for (const m of rawMatches) {
      if (m.start <= segStart && segEnd <= m.end) {
        if (!winner || m.priority > winner.priority) winner = m;
      }
    }
    if (!winner) continue;
    const last = segments[segments.length - 1];
    if (last && last.end === segStart && last.color === winner.color) {
      last.end = segEnd;
    } else {
      segments.push({
        start: segStart,
        end: segEnd,
        color: winner.color,
        priority: winner.priority,
      });
    }
  }
  return segments;
};

const highlightStyleFor = (color: HighlightColor): string => {
  const { backgroundColor, borderBottom } = highlightStyles[color];
  return `--lexical-highlight-bg: ${backgroundColor}; --lexical-highlight-border: ${borderBottom};`;
};

const $applyHighlightToTextNode = (
  textNode: TextNode,
  patterns: CompiledPattern[],
): void => {
  const text = textNode.getTextContent();
  if (!text) return;

  const matches = collectMatches(text, patterns);
  if (matches.length === 0) return;

  const offsets: number[] = [];
  for (const m of matches) {
    if (m.start > 0 && m.start < text.length) offsets.push(m.start);
    if (m.end > 0 && m.end < text.length) offsets.push(m.end);
  }

  const segments =
    offsets.length === 0 ? [textNode] : textNode.splitText(...offsets);

  let cursor = 0;
  for (const segment of segments) {
    const segText = segment.getTextContent();
    const segStart = cursor;
    const segEnd = cursor + segText.length;
    cursor = segEnd;

    const match = matches.find((m) => m.start <= segStart && segEnd <= m.end);
    if (!match) continue;

    segment.toggleFormat('highlight');
    if (match.color) {
      segment.setStyle(highlightStyleFor(match.color));
    }
  }
};

export const HighlightPlugin = ({
  patterns,
}: {
  patterns: HighlightPattern[];
}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const compiled = compilePatterns(patterns);

    const unregisterTransform = editor.registerNodeTransform(
      TextNode,
      (textNode) => {
        if (textNode.hasFormat('highlight')) return;
        if (compiled.length === 0) return;
        $applyHighlightToTextNode(textNode, compiled);
      },
    );

    editor.update(() => {
      const root = $getRoot();
      traverseTextNodes(root, (textNode) => {
        if (textNode.hasFormat('highlight')) {
          textNode.toggleFormat('highlight');
          textNode.setStyle('');
        }
        textNode.markDirty();
      });
    });

    return unregisterTransform;
  }, [editor, patterns]);

  return null;
};
